import React, { useEffect, useState } from 'react';
import { Form, Input } from 'semantic-ui-react';
import { pyround } from 'src/utils/useful_functions';

/** Clamp precision to the supported 0–6 range (safe for Number.toFixed). */
export function normalizePrecision(precision: any): number {
  const n = Number(precision);
  if (!Number.isFinite(n)) return 0;
  return Math.min(6, Math.max(0, Math.trunc(n)));
}

export function formatCustomNumber(
  value: any,
  precision: number = 0
): string {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  const digits = normalizePrecision(precision);
  return pyround(num, digits).toFixed(digits);
}

export function roundCustomNumber(
  value: any,
  precision: number = 0
): number | null {
  if (value == null || value === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return pyround(num, normalizePrecision(precision));
}

/** Immediate validation message for a number custom-field value. */
export function numberFieldError(
  value: any,
  field: {
    min?: number | null;
    max?: number | null;
    precision?: number;
  }
): string | null {
  if (value === '' || value == null) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Must be a valid number';
  const precision = normalizePrecision(field.precision);
  if (field.min != null && num < field.min) {
    return `Must be at least ${formatCustomNumber(field.min, precision)}`;
  }
  if (field.max != null && num > field.max) {
    return `Must be at most ${formatCustomNumber(field.max, precision)}`;
  }
  return null;
}

type Props = {
  value: any;
  precision?: number;
  min?: number | null;
  max?: number | null;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  onChange: (value: number | null | string) => void;
};

/**
 * Number input for custom fields that rounds and displays to the
 * field's configured decimal precision.
 */
export function CustomNumberInput({
  value,
  precision = 0,
  min,
  max,
  disabled,
  error,
  placeholder,
  onChange,
}: Props) {
  const digits = normalizePrecision(precision);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatCustomNumber(value, digits));

  useEffect(() => {
    if (!focused) {
      setText(formatCustomNumber(value, digits));
    }
  }, [value, digits, focused]);

  const commit = () => {
    setFocused(false);
    if (text.trim() === '') {
      onChange(null);
      setText('');
      return;
    }
    const rounded = roundCustomNumber(text, digits);
    if (rounded == null) {
      onChange(text);
      return;
    }
    onChange(rounded);
    setText(formatCustomNumber(rounded, digits));
  };

  return (
    <Input
      type="number"
      value={focused ? text : formatCustomNumber(value, digits)}
      min={min ?? undefined}
      max={max ?? undefined}
      step={Math.pow(10, -digits)}
      disabled={disabled}
      error={error}
      placeholder={placeholder}
      onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        setText(
          value == null || value === ''
            ? ''
            : formatCustomNumber(value, digits)
        );
        e.target.select();
      }}
      onChange={(_, { value: next }) => {
        setText(next);
        if (next === '') {
          onChange(null);
          return;
        }
        const parsed = parseFloat(next);
        onChange(Number.isNaN(parsed) ? next : parsed);
      }}
      onBlur={commit}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

type LabeledProps = Props & {
  label: React.ReactNode;
  error?: string | null;
};

/**
 * `CustomNumberInput` wrapped in a `Form.Field`, showing the field label
 * and, when invalid, a red border plus an error message below the input
 * (matching the Project Settings error style).
 */
export function LabeledCustomNumberInput({
  label,
  error,
  ...inputProps
}: LabeledProps) {
  return (
    <Form.Field error={!!error}>
      <label>{label}</label>
      <CustomNumberInput {...inputProps} error={!!error} />
      {error && <div style={{ color: '#9f3a38', marginTop: 4 }}>{error}</div>}
    </Form.Field>
  );
}
